/**
 * render/toasts.ts — Toast Notifications
 *
 * Warm, unobtrusive notifications that slide in from the right edge.
 * Loot drops glint gold. Level ups pulse green. Discoveries shimmer blue.
 * They stack from the bottom-right, hold for a moment, then slide away.
 *
 * Never modal, never blocking, never annoying. Just a gentle nudge
 * that something good happened while you were busy being here.
 */

import type { GameState, Toast } from "../engine/state.js";

// ─── Constants ──────────────────────────────────────────────────────────────

/** Maximum toasts stored in the queue */
const MAX_QUEUE = 8;

/** Maximum visible toasts on screen at once */
const MAX_VISIBLE = 4;

/** Slide-in animation duration (seconds) */
const SLIDE_IN = 0.2;

/** Slide-out animation duration (seconds) */
const SLIDE_OUT = 0.2;

/** Toast panel styling */
const BG_COLOR = "rgba(30, 20, 12, 0.88)";
const BORDER_COLOR = "rgba(200, 160, 80, 0.4)";
const TEXT_COLOR = "#e8d0a8";
const FONT = "12px sans-serif";
const CORNER_RADIUS = 6;
const PAD_X = 8;
const PAD_Y = 6;
const MAX_WIDTH = 260;
const ACCENT_WIDTH = 2;
const TOAST_GAP = 4;
const MARGIN_RIGHT = 12;
const MARGIN_BOTTOM = 60; // above the action bar

/** Accent colors by toast type */
const ACCENT_COLORS: Record<Toast["type"], string> = {
  loot:        "#d4a843",  // gold
  levelup:     "#5cb85c",  // green
  discovery:   "#5b9bd5",  // blue
  achievement: "#9b59b6",  // purple
  currency:    "#e6a817",  // amber
  info:        "#888888",  // grey
  system:      "#c0a060",  // warm tan
  pvp:         "#e88020",  // orange
  death:       "#a04040",  // deep red
};

// ─── Public API ─────────────────────────────────────────────────────────────

let toastCounter = 0;

/**
 * Push a toast notification into the state queue.
 * Caps the array at MAX_QUEUE, dropping oldest toasts first.
 */
export function addToast(
  state: GameState,
  text: string,
  type: Toast["type"],
  duration: number = 3,
): void {
  const toast: Toast = {
    id: `toast_${++toastCounter}_${Date.now().toString(36)}`,
    text,
    type,
    time: performance.now() / 1000,
    duration,
  };

  state.toasts.push(toast);

  // Cap at MAX_QUEUE — drop oldest
  while (state.toasts.length > MAX_QUEUE) {
    state.toasts.shift();
  }
}

/**
 * Render active toasts. Stacked from bottom-right, sliding in from the right,
 * sliding out to the left. Cleans up expired toasts each frame.
 */
export function drawToasts(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  canvasW: number,
  canvasH: number,
  timestamp: number,
): void {
  const now = timestamp / 1000;

  // Clean up fully expired toasts (past slide-out)
  state.toasts = state.toasts.filter(t => {
    const elapsed = now - t.time;
    return elapsed < t.duration + SLIDE_OUT;
  });

  // Only render the most recent MAX_VISIBLE
  const visible = state.toasts.slice(-MAX_VISIBLE);
  if (visible.length === 0) return;

  ctx.save();
  ctx.font = FONT;

  // Measure each toast to get its height, then stack from bottom
  const measured: Array<{
    toast: Toast;
    textLines: string[];
    totalW: number;
    totalH: number;
  }> = [];

  for (const toast of visible) {
    const lines = wrapText(ctx, toast.text, MAX_WIDTH - PAD_X * 2 - ACCENT_WIDTH - 4);
    const lineH = 15;
    const totalH = PAD_Y * 2 + lines.length * lineH;
    let maxLineW = 0;
    for (const line of lines) {
      const w = ctx.measureText(line).width;
      if (w > maxLineW) maxLineW = w;
    }
    const totalW = Math.min(MAX_WIDTH, ACCENT_WIDTH + 4 + PAD_X * 2 + maxLineW);
    measured.push({ toast, textLines: lines, totalW, totalH });
  }

  // Stack from bottom-right, going upward
  let stackY = canvasH - MARGIN_BOTTOM;

  for (let i = measured.length - 1; i >= 0; i--) {
    const { toast, textLines, totalW, totalH } = measured[i];
    const elapsed = now - toast.time;

    // Compute horizontal offset for slide animation
    let offsetX = 0;
    if (elapsed < SLIDE_IN) {
      // Sliding in from right
      const t = elapsed / SLIDE_IN;
      const ease = 1 - (1 - t) * (1 - t); // ease-out quad
      offsetX = (1 - ease) * (totalW + MARGIN_RIGHT);
    } else if (elapsed > toast.duration) {
      // Sliding out to left
      const t = (elapsed - toast.duration) / SLIDE_OUT;
      const ease = t * t; // ease-in quad
      offsetX = -ease * (totalW + MARGIN_RIGHT);
    }

    const x = canvasW - MARGIN_RIGHT - totalW + offsetX;
    const y = stackY - totalH;

    // Background panel
    ctx.fillStyle = BG_COLOR;
    roundRect(ctx, x, y, totalW, totalH, CORNER_RADIUS);
    ctx.fill();

    // Border
    ctx.strokeStyle = BORDER_COLOR;
    ctx.lineWidth = 1;
    roundRect(ctx, x, y, totalW, totalH, CORNER_RADIUS);
    ctx.stroke();

    // Left accent bar
    const accentColor = ACCENT_COLORS[toast.type] || ACCENT_COLORS.info;
    ctx.fillStyle = accentColor;
    ctx.fillRect(x + 1, y + CORNER_RADIUS, ACCENT_WIDTH, totalH - CORNER_RADIUS * 2);

    // Text
    ctx.fillStyle = TEXT_COLOR;
    ctx.textBaseline = "top";
    const textX = x + ACCENT_WIDTH + 4 + PAD_X;
    const lineH = 15;
    for (let li = 0; li < textLines.length; li++) {
      ctx.fillText(textLines[li], textX, y + PAD_Y + li * lineH);
    }

    stackY = y - TOAST_GAP;
  }

  ctx.restore();
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Word-wrap text into lines that fit within maxWidth. */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const test = currentLine ? currentLine + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = test;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : [""];
}

/** Draw a rounded rectangle path (does not fill/stroke). */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
